import type {
  Audit,
  WebsiteAuditSummary,
  BrandNarrativeSummary,
  BrandAuthoritySummary,
  BrandReferralSummary,
  EarnedMediaAuditSummary,
} from './audit';
import type { Brand, CompetitorAnalysis } from './brand';
import type { Task } from './task';

/**
 * Agency-facing white-label branding pulled onto every report payload so the
 * frontend / export pipeline don't need a second round-trip to the tenant
 * record. Values mirror `TenantSettings.agencyBranding` (minus the raw logo
 * bytes — frontend fetches the logo through the dedicated endpoint).
 */
export interface ReportBranding {
  agencyName?: string;
  primaryColor?: string;
  footerText?: string;
  logoUrl?: string;
}

/**
 * Report types exposed to the frontend.
 * - `audit` = point-in-time snapshot of the latest completed audit per axis.
 * - `status` = date-range evolution (series + tasks done/in-progress + focus).
 */
export type ReportType = 'audit' | 'status';

// --- AUDIT REPORT --------------------------------------------------------

export interface AuditReportBrandSnapshot {
  brandId: string;
  name: string;
  url?: string;
  faviconUrl?: string;
  industry?: string;
  description?: string;
}

export interface AuditReportWebsiteBlock {
  auditId: string;
  completedAt: string;
  summary: WebsiteAuditSummary;
}

export interface AuditReportNarrativeBlock {
  auditId: string;
  completedAt: string;
  summary: BrandNarrativeSummary;
}

export interface AuditReportAuthorityBlock {
  auditId: string;
  completedAt: string;
  summary: BrandAuthoritySummary;
}

export interface AuditReportReferralBlock {
  auditId: string;
  completedAt: string;
  summary: BrandReferralSummary;
}

export interface AuditReportEarnedMediaBlock {
  auditId: string;
  completedAt: string;
  summary: EarnedMediaAuditSummary;
}

export interface AuditReportCompetitorBlock {
  name: string;
  url?: string;
  analyzedAt: string;
  positioning: string;
  strengths: string[];
  weaknesses: string[];
  sharedTopics: string[];
  uniqueToCompetitor: string[];
}

/**
 * One actor's AI share-of-voice across the three Brand Perception axes, derived
 * from the `competitor_matrix` footers embedded in each axis summary (Class A —
 * pure aggregation, zero marginal LLM). Mirrors the "Concurrents suivis" table
 * on the Concurrents page so the report and the live view agree. A `null` axis
 * value means the competitor had no data on that axis (rendered as "—").
 */
export interface AuditReportCompetitorRow {
  name: string;
  /** Normalized domain (for the favicon), when the competitor is still tracked. */
  domain?: string;
  /** Referral share-of-voice: presentOn / promptCount × 100. */
  referralPdv: number | null;
  /** Referral prompts where the actor was recommended (ranked). */
  recommendedOn?: number;
  /** Mean rank across referral prompts where ranked. */
  avgPosition?: number;
  /** Authority presence: presentOn / promptCount × 100. */
  authorityPct: number | null;
  /** Narrative co-citations alongside the brand (count) over `narrativePromptCount` prompts. */
  narrativeCoCited: number;
  narrativePromptCount: number;
}

/** The brand's own share-of-voice, computed the same way for side-by-side context. */
export interface AuditReportBrandSov {
  referralPdv: number | null;
  authorityPct: number | null;
  narrativeCoCited: number;
  narrativePromptCount: number;
}

/** An untracked brand the engines recommend (referral `suggested_competitors`). */
export interface AuditReportCompetitorSuggestion {
  name: string;
  citedIn: number;
  promptCount: number;
  domain?: string;
}

/**
 * Modern competitor angle: AI-visibility ranking from the Brand Perception
 * matrix. Distinct from `AuditReportCompetitorBlock[]` (legacy manual
 * strengths/weaknesses analyses) — both can coexist on a report. Omitted when
 * no tracked competitor has any matrix data.
 */
export interface AuditReportCompetitorRanking {
  /** Most recent axis-completion timestamp the ranking was computed from. */
  computedAt: string;
  /** The brand's own share-of-voice (the ⭐ row), for comparison. */
  brand: AuditReportBrandSov;
  /** Tracked competitors ranked by Referral PdV descending (no-data last). */
  competitors: AuditReportCompetitorRow[];
  /** Untracked brands the engines recommend, not yet tracked. */
  suggested: AuditReportCompetitorSuggestion[];
}

/**
 * "Rapport d'audit" — latest-completed snapshot across all axes.
 * Every axis block is independently optional: if no completed audit exists
 * for a given axis the block is omitted rather than zero-filled, so the
 * frontend can render "not yet analysed" states distinctly from "score = 0".
 */
export interface AuditReportPayload {
  reportType: 'audit';
  generatedAt: string;
  /**
   * Language the report is rendered in (fr/en). Chosen per export (default =
   * brand working language). Drives BOTH the deterministic template prose in
   * `reportExport.ts` AND the LLM `executiveNarrative`. Absent ⇒ French.
   */
  language?: 'fr' | 'en';
  branding: ReportBranding;
  brand: AuditReportBrandSnapshot;
  /**
   * LLM-authored cross-axis executive summary (Nemotron 120B), written ONCE at
   * report-build time — NOT per export. Optional + fail-open: when absent (model
   * error, or no axis scored yet) the exporter falls back to the deterministic
   * `auditExecutiveSummary` template. Never regenerated at download time.
   */
  executiveNarrative?: string;
  website?: AuditReportWebsiteBlock;
  narrative?: AuditReportNarrativeBlock;
  authority?: AuditReportAuthorityBlock;
  referral?: AuditReportReferralBlock;
  earnedMedia?: AuditReportEarnedMediaBlock;
  /** Legacy manual per-competitor strengths/weaknesses analyses (may be empty). */
  competitors: AuditReportCompetitorBlock[];
  /** Modern AI-visibility ranking from the Brand Perception matrix (Class A). */
  competitorRanking?: AuditReportCompetitorRanking;
  /**
   * Render hint from the report template: when false, the exporter omits the
   * cover KPI score grid. Defaults to true (undefined ⇒ shown). Set by
   * applyAuditTemplate; not produced by the report builder.
   */
  showScores?: boolean;
  /**
   * Render hint from the report template: when true, the exporter appends a
   * "Prompts utilisés" section listing the prompts tested per Brand Perception
   * axis (read from the axis summaries' `query_scores`). Opt-in — defaults to
   * OFF (undefined ⇒ hidden). Set by applyAuditTemplate.
   */
  showPromptsUsed?: boolean;
  /**
   * Render hint: when true, the Website section renders the AI business-readability
   * synthesis (Phase D — what AI understands the business to be, confidence,
   * missing strategy topics, recommendations). Opt-in — defaults to OFF.
   */
  showWebsiteReadability?: boolean;
  /**
   * Render hint: when true, the Website section renders the per-signal
   * content-quality averages table (statistics, citations, readability…).
   * Opt-in — defaults to OFF.
   */
  showWebsiteSignals?: boolean;
}

// --- STATUS REPORT -------------------------------------------------------

/**
 * One datapoint in a score-over-time series. `auditId` lets the frontend
 * deep-link from a sparkline tick back to the originating audit detail page.
 */
export interface ReportSeriesPoint {
  auditId: string;
  completedAt: string;
  score: number;
}

export type ReportAxisKey =
  | 'website'
  | 'narrative'
  | 'authority'
  | 'referral'
  | 'earned_media';

/** Series for each scored axis over the selected window. */
export type ReportSeries = Record<ReportAxisKey, ReportSeriesPoint[]>;

/**
 * "Rapport d'activité" — date-range evolution + task progress + focus.
 * `windowStart` / `windowEnd` are ISO timestamps (inclusive start,
 * exclusive end). `focusTasks` are the user-curated 3/6/12-month priorities
 * explicitly selected in the frontend; `tasksInProgress` / `tasksCompleted`
 * are derived from DynamoDB filtered by the window.
 */
export interface StatusReportPayload {
  reportType: 'status';
  generatedAt: string;
  /** Language the report is rendered in (fr/en). See AuditReportPayload.language. */
  language?: 'fr' | 'en';
  windowStart: string;
  windowEnd: string;
  branding: ReportBranding;
  brand: AuditReportBrandSnapshot;
  /**
   * LLM-authored cross-axis trajectory summary (Nemotron 120B), written ONCE at
   * report-build time. Optional + fail-open: absent ⇒ exporter falls back to the
   * deterministic `statusExecutiveSummary` template. See AuditReportPayload.
   */
  executiveNarrative?: string;
  series: ReportSeries;
  tasksCompleted: Task[];
  tasksInProgress: Task[];
  focusTasks: Task[];
}

// --- Request inputs ------------------------------------------------------

export interface AuditReportQuery {
  brandId: string;
}

export interface StatusReportQuery {
  brandId: string;
  /** ISO timestamp, inclusive. Defaults to windowEnd - 90 days server-side. */
  windowStart?: string;
  /** ISO timestamp, exclusive. Defaults to now server-side. */
  windowEnd?: string;
  /** Optional taskIds the user marked as their 3/6/12-month focus. */
  focusTaskIds?: string[];
}

// --- Helpers -------------------------------------------------------------

/**
 * Shape guarantees used by the PDF / PPTX export pipeline — if you add a
 * new axis or block, extend this helper so export code can iterate without
 * hand-coding key lists.
 */
export const REPORT_AXIS_KEYS: ReportAxisKey[] = [
  'website',
  'narrative',
  'authority',
  'referral',
  'earned_media',
];

export function emptyReportSeries(): ReportSeries {
  return {
    website: [],
    narrative: [],
    authority: [],
    referral: [],
    earned_media: [],
  };
}

/**
 * Types-only consumers don't need these re-exports but they make agent/util
 * code simpler when handling a mixed payload.
 */
export type AnyReportPayload = AuditReportPayload | StatusReportPayload;
export type { Audit, Brand, CompetitorAnalysis, Task };

// ───────────────────────── Stored report library ─────────────────────────

export type ReportKind = 'audit' | 'status';
export type ReportFormat = 'pdf' | 'pptx';

/** Fixed lifetime of a public share link (days). Revocable before expiry. */
export const REPORT_SHARE_TTL_DAYS = 30;

/**
 * Make a report name safe for a `Content-Disposition` filename. HTTP header
 * values must be ISO-8859-1; report names routinely carry non-Latin-1 chars
 * (e.g. the em-dash in "Audit — Brand"), which Node's `setHeader` rejects with
 * ERR_INVALID_CHAR → 500. Strips anything outside `[word . - space]` and caps
 * length. Shared by every report download path so they stay consistent.
 */
export function sanitizeReportFilename(name: string): string {
  return (name || 'report').replace(/[^\w.\- ]+/g, '_').slice(0, 120).trim() || 'report';
}

/**
 * A generated report persisted to S3 (browser renders the bytes, server stores
 * + bills generation). Metadata row on the audits table:
 *   PK = TENANT#<tenantId>, SK = REPORT#<reverse-ts>#<reportId>
 * Bytes at s3://<AGENT_DATA_BUCKET>/tenants/<t>/brands/<b>/reports/<reportId>.<ext>.
 * Generation is credit-billed (report_export); download is free.
 */
export interface StoredReport {
  reportId: string;
  tenantId: string;
  brandId: string;
  brandName?: string;
  kind: ReportKind;
  format: ReportFormat;
  name: string; // user-editable display name
  sizeBytes: number;
  createdAt: string;
  createdBy?: string; // userId
  /**
   * Set while a public share link is active. ISO timestamp 30 days out from
   * the last "Share" action. The link is a separate opaque bearer token (a
   * server-stored random nonce) — only its expiry surfaces here so the UI can
   * show "Lien actif · expire le …" + a Revoke control. Absent ⇒ not shared.
   */
  shareExpiresAt?: string;
}

// ───────────────────────── Report template ─────────────────────────
// A per-tenant template configures WHICH sections appear in generated reports.
// It is NOT a separate renderer: every existing renderer already skips an
// absent payload block, so "disable a section" == "delete that key from the
// payload before rendering" (applyAuditTemplate / applyStatusTemplate below).
// reportExport.ts therefore needs zero changes.

/** Toggleable sections of the AUDIT (point-in-time snapshot) report. */
export type AuditReportSection =
  | 'scores' // synthesis score grid (cover KPIs)
  | 'website'
  | 'websiteReadability' // opt-in: AI business-readability synthesis (Phase D)
  | 'websiteSignals' // opt-in: per-signal content-quality averages
  | 'narrative'
  | 'authority'
  | 'referral'
  | 'earnedMedia'
  | 'competitorRanking' // AI-visibility matrix (Class A)
  | 'competitors' // legacy manual strengths/weaknesses analyses
  | 'promptsUsed'; // appendix listing the prompts tested per Brand Perception axis

/** Toggleable sections of the STATUS (evolution-over-time) report. */
export type StatusReportSection =
  | 'evolution' // per-axis score series
  | 'tasksCompleted'
  | 'tasksInProgress'
  | 'focusTasks';

export const AUDIT_REPORT_SECTIONS: AuditReportSection[] = [
  'scores',
  'website',
  'websiteReadability',
  'websiteSignals',
  'narrative',
  'authority',
  'referral',
  'earnedMedia',
  'competitorRanking',
  'competitors',
  'promptsUsed',
];

/**
 * Sections that default to DISABLED (opt-in) rather than the usual
 * "on unless explicitly false". `promptsUsed` is an appendix most reports don't
 * want, so it only appears when the tenant explicitly turns it on.
 */
export const AUDIT_OPT_IN_SECTIONS: AuditReportSection[] = [
  'promptsUsed',
  'websiteReadability',
  'websiteSignals',
];

export const STATUS_REPORT_SECTIONS: StatusReportSection[] = [
  'evolution',
  'tasksCompleted',
  'tasksInProgress',
  'focusTasks',
];

/**
 * Per-tenant report template (white-label section config). One template for the
 * whole agency — consistent deliverable across every client brand, mirroring how
 * `agencyBranding` is tenant-level. Stored on `TenantSettings.reportTemplate`.
 * A missing section key defaults to ENABLED (so older tenants / new sections
 * render by default; the editor only needs to persist explicit opt-outs).
 */
export interface ReportTemplate {
  audit: Partial<Record<AuditReportSection, boolean>>;
  status: Partial<Record<StatusReportSection, boolean>>;
}

/** A section is on unless the template explicitly sets it to false. */
export function isSectionEnabled(
  flags: Partial<Record<string, boolean>> | undefined,
  section: string,
): boolean {
  return flags?.[section] !== false;
}

/**
 * Mask an audit payload to the template: drop every block whose section is
 * disabled. Renderers skip absent keys, so this is the whole mechanism. `scores`
 * is a render hint (the cover KPI grid), carried back on the payload so the
 * exporter can honor it without inspecting the template.
 */
export function applyAuditTemplate(
  payload: AuditReportPayload,
  tmpl: ReportTemplate | undefined,
): AuditReportPayload {
  const f = tmpl?.audit;
  if (!f) return payload;
  const out: AuditReportPayload = { ...payload };
  if (!isSectionEnabled(f, 'website')) delete out.website;
  if (!isSectionEnabled(f, 'narrative')) delete out.narrative;
  if (!isSectionEnabled(f, 'authority')) delete out.authority;
  if (!isSectionEnabled(f, 'referral')) delete out.referral;
  if (!isSectionEnabled(f, 'earnedMedia')) delete out.earnedMedia;
  if (!isSectionEnabled(f, 'competitorRanking')) delete out.competitorRanking;
  if (!isSectionEnabled(f, 'competitors')) out.competitors = [];
  out.showScores = isSectionEnabled(f, 'scores');
  // Opt-in: hidden unless the template explicitly enables it (=== true).
  out.showPromptsUsed = f.promptsUsed === true;
  out.showWebsiteReadability = f.websiteReadability === true;
  out.showWebsiteSignals = f.websiteSignals === true;
  return out;
}

/** Mask a status payload to the template — same principle as applyAuditTemplate. */
export function applyStatusTemplate(
  payload: StatusReportPayload,
  tmpl: ReportTemplate | undefined,
): StatusReportPayload {
  const f = tmpl?.status;
  if (!f) return payload;
  const out: StatusReportPayload = { ...payload };
  if (!isSectionEnabled(f, 'evolution')) out.series = emptyReportSeries();
  if (!isSectionEnabled(f, 'tasksCompleted')) out.tasksCompleted = [];
  if (!isSectionEnabled(f, 'tasksInProgress')) out.tasksInProgress = [];
  if (!isSectionEnabled(f, 'focusTasks')) out.focusTasks = [];
  return out;
}
